import React, { useState, useCallback, useMemo, cloneElement } from 'react';
import { castArray, isEmpty, isEqual, pick, round, startCase } from 'lodash';
import classnames from 'classnames';
import {
  Modal,
  Button,
  Upload,
  UploadProps,
  Form,
  Input,
  notification,
  Space,
  Tooltip,
  TooltipProps,
  FormInstance,
  Progress,
  theme,
} from 'antd';
import { EyeOutlined, DeleteOutlined, FileTextOutlined } from '@ant-design/icons';
import { faInbox } from '@fortawesome/free-solid-svg-icons';
import useApi from '../../api';
import { Rule } from '../../utils/rules';
import { Formatter } from '../../utils';
import FaIcon from '../FaIcon';
import './styles.css';

const MAX_UPLOAD_COUNT = 1;
const FILE_PART_SIZE = 5 * 1024 * 1024; // 5MB per chunk (S3 multipart minimum)
const MIN_FILE_SIZE = 5 * 1024 * 1024; // 5MB — below this, upload in a single request
const MAX_RETRY = 5;
const MIMES = {
  VIDEO: {
    types: ['video/mp4'],
    extensions: ['.mp4'],
  },
  CSV: {
    types: ['text/csv', 'application/vnd.ms-excel'],
    extensions: ['.csv'],
  },
};
enum UploadStatusEnum {
  UPLOADING = 'UPLOADING',
  UPLOADED = 'UPLOADED',
}

interface UploadFileData {
  uploadId: string | null;
  fileData: Blob[];
  uploadedParts: {
    PartNumber: number;
  }[];
  failedRetryCount: number;
}

export type FileData = {
  id: string;
  url: string;
};

interface FormUploadProps {
  name: string;
  /** populated data field */
  dataField?: string;
  label?: string;
  type: 'VIDEO' | 'CSV';
  apiVariables?: any;
  rules?: Rule[];
  disabled?: boolean;
  tooltip?: TooltipProps;
  getForm: () => FormInstance<any>;
  trigger?: JSX.Element;
  titleExtra?: JSX.Element;
  videoUrl?: string;
  onVideoAdd?: (val: boolean) => void;
  onUpload?: (fileData: FileData) => void;
  getVideoDimensions?: (height: number, width: number) => void;
}
export interface VideoURLData {
  fileId: string;
  file: FileData;
  thumbnailId: string;
  thumbnail: FileData;
  title: any;
  duration: number;
}

function FormMultiPartUpload(props: FormUploadProps) {
  const {
    name,
    dataField,
    label,
    type,
    apiVariables,
    rules,
    disabled,
    getForm,
    tooltip,
    trigger,
    titleExtra,
    videoUrl,
    onVideoAdd,
    onUpload,
    getVideoDimensions,
  } = props;

  const getDefaultFileUploadData = (): UploadFileData => ({
    uploadId: null,
    uploadedParts: [],
    fileData: [],
    failedRetryCount: 0,
  });

  const [uploadFileData, setUploadFileData] = useState<UploadFileData>(getDefaultFileUploadData);
  const [fileList, setFileList] = useState<any[]>([]);
  const [visible, setVisible] = useState(false);
  const [previewItem, setPreviewItem] = useState<string | null>(null);
  const [uploadPercentage, setUploadPercentage] = useState<number>(0);
  const [uploadStatus, setUploadStatus] = useState<UploadStatusEnum | null>(null);
  const [isVideoAdded, setVideoAdded] = useState<boolean>(false);
  const uploadRunRef = React.useRef(0);
  const { token } = theme.useToken();
  const [uploadFilePart] = useApi.File.UploadFilePart();
  const [uploadFile] = useApi.File.Upload();
  const [cancelMultiPartUpload] = useApi.File.CancelMultiUpload({ notification: true });

  const fileUploadError = useCallback(() => {
    notification.error({
      message: 'File Upload Error.',
      description: 'Error occured in file uploading.',
    });
  }, []);

  const handleBeforeUpload = useCallback<NonNullable<UploadProps['beforeUpload']>>(
    async (file) => {
      uploadRunRef.current += 1;
      if (file.size > MIN_FILE_SIZE) {
        const tmpFileData: Blob[] = [];
        for (let start = 0; start < file.size; start += FILE_PART_SIZE) {
          tmpFileData.push(file.slice(start, start + FILE_PART_SIZE));
        }
        setUploadFileData({
          uploadId: '',
          fileData: tmpFileData,
          uploadedParts: [],
          failedRetryCount: 0,
        });
      }
      setFileList(() => [file]);
      setUploadStatus(null);
      setUploadPercentage(0);
      onVideoAdd?.(true);
      setVideoAdded(true);
      return false;
    },
    [onVideoAdd],
  );

  const handleOnRemove = useCallback<NonNullable<UploadProps['onRemove']>>(
    (file) => {
      uploadRunRef.current += 1;
      setFileList((oldList) => {
        const index = oldList.indexOf(file);
        const newFileList = oldList.slice();
        newFileList.splice(index, 1);
        return newFileList;
      });
      setUploadFileData(getDefaultFileUploadData());
      setUploadPercentage(0);
      setVideoAdded(false);
      onVideoAdd?.(false);
      setUploadStatus(null);
    },
    [onVideoAdd],
  );

  const completeFileUplaod = useCallback(
    (file: FileData) => {
      onUpload?.(file);
      const form = getForm();
      const newData: any = {};
      newData[name] = file.id;
      if (dataField) newData[dataField] = pick(file, ['id', 'url']);
      form?.setFieldsValue(newData);
      setUploadPercentage(100);
      setVisible(false);
      setUploadStatus(UploadStatusEnum.UPLOADED);
      notification.success({ message: 'File Uploaded.' });
    },
    [dataField, getForm, name, onUpload],
  );

  const uploadSingleFile = useCallback(async (): Promise<FileData> => {
    const formData = new FormData();
    fileList.forEach((file) => {
      formData.append('files', file);
    });
    Object.keys(apiVariables).forEach((key) => formData.append(key, apiVariables[key]));
    const { data }: { data: FileData[] | null } = await uploadFile({ data: formData });
    if (!data?.length) throw new Error('Upload returned no file');
    return data[0];
  }, [fileList, apiVariables, uploadFile]);

  const uploadMultiFileParts = useCallback(
    async (runId: number): Promise<FileData | null> => {
      const totalParts = uploadFileData.fileData.length;
      let fileId = uploadFileData.uploadId;
      let uploadedFile: FileData | null = null;
      const locallyUploadedPartNumbers = new Set(
        uploadFileData.uploadedParts.map((part) => part.PartNumber),
      );
      const pendingPartNumbers = Array.from({ length: totalParts }, (_, index) => index + 1).filter(
        (partNumber) => !locallyUploadedPartNumbers.has(partNumber),
      );
      if (!pendingPartNumbers.length) pendingPartNumbers.push(totalParts);
      const retryCounts = new Map<number, number>();
      let recoveryCount = 0;

      while (pendingPartNumbers.length) {
        if (uploadRunRef.current !== runId) return null;
        const partNumber = pendingPartNumbers.shift() as number;

        try {
          const formData = new FormData();
          const file = new File([uploadFileData.fileData[partNumber - 1]], fileList[0].name, {
            type: fileList[0].type,
          });
          formData.append('file', file);
          formData.append('partNumber', `${partNumber}`);
          formData.append('totalParts', `${totalParts}`);
          formData.append('isLastPart', `${partNumber === totalParts}`);
          if (fileId) formData.append('id', fileId);
          Object.keys(apiVariables).forEach((key) => formData.append(key, apiVariables[key]));

          const result: { data: FileData } = await uploadFilePart({ data: formData });
          if (!result.data?.id) throw new Error('Upload returned no file id');
          if (uploadRunRef.current !== runId) return null;

          const uploadedFileId = result.data.id;
          fileId = uploadedFileId;
          uploadedFile = result.data;
          retryCounts.delete(partNumber);
          setUploadFileData((oldUploadFileData) => ({
            ...oldUploadFileData,
            uploadId: uploadedFileId,
            uploadedParts: [
              ...oldUploadFileData.uploadedParts.filter((part) => part.PartNumber !== partNumber),
              { PartNumber: partNumber },
            ],
            failedRetryCount: 0,
          }));
          setUploadPercentage((oldPercentage) =>
            Math.max(oldPercentage, parseInt(String((partNumber / totalParts) * 100), 10)),
          );
        }
        catch (error: any) {
          if (uploadRunRef.current !== runId) return null;
          const missingPartNumbers = error?.response?.data?.missingPartNumbers;
          if (
            partNumber === totalParts &&
            Array.isArray(missingPartNumbers) &&
            missingPartNumbers.length &&
            recoveryCount < MAX_RETRY
          ) {
            const recoveryPartNumbers = missingPartNumbers.filter(
              (missingPartNumber) =>
                Number.isInteger(missingPartNumber) &&
                missingPartNumber > 0 &&
                missingPartNumber <= totalParts,
            );
            recoveryCount += 1;
            pendingPartNumbers.unshift(
              ...Array.from(new Set<number>([...recoveryPartNumbers, totalParts])),
            );
            continue;
          }

          const failedRetryCount = (retryCounts.get(partNumber) || 0) + 1;
          retryCounts.set(partNumber, failedRetryCount);
          setUploadFileData((oldUploadFileData) => ({
            ...oldUploadFileData,
            failedRetryCount,
          }));
          if (failedRetryCount > MAX_RETRY) throw error;
          pendingPartNumbers.unshift(partNumber);
        }
      }

      return uploadedFile;
    },
    [
      uploadFileData.uploadedParts,
      uploadFileData.fileData,
      uploadFileData.uploadId,
      fileList,
      apiVariables,
      uploadFilePart,
    ],
  );

  const uploadFiles = useCallback(async () => {
    if (!isVideoAdded || !fileList.length) {
      notification.error({ message: `Please select a ${type === 'CSV' ? 'file' : 'video'}` });
      return;
    }

    const runId = uploadRunRef.current + 1;
    uploadRunRef.current = runId;
    setUploadStatus(UploadStatusEnum.UPLOADING);
    setUploadPercentage(1);
    setUploadFileData((oldUploadFileData) => ({ ...oldUploadFileData, failedRetryCount: 0 }));

    // Video-only: extract duration & dimensions before upload begins.
    if (type === 'VIDEO') {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        getForm()?.setFieldsValue({ duration: Formatter.durationStr(round(video.duration ?? 0)) });
        getVideoDimensions?.(video.videoHeight, video.videoWidth);
      };
      // @ts-ignore
      video.src = URL.createObjectURL(fileList[0]);
    }

    try {
      const uploadedFile = uploadFileData.fileData.length
        ? await uploadMultiFileParts(runId)
        : await uploadSingleFile();
      if (uploadedFile && uploadRunRef.current === runId) completeFileUplaod(uploadedFile);
    }
    catch {
      if (uploadRunRef.current !== runId) return;
      setUploadPercentage(-1);
      setUploadStatus(null);
      fileUploadError();
    }
  }, [
    completeFileUplaod,
    fileList,
    fileUploadError,
    getForm,
    getVideoDimensions,
    isVideoAdded,
    type,
    uploadFileData.fileData.length,
    uploadMultiFileParts,
    uploadSingleFile,
  ]);

  const cancelFileUpload = useCallback(() => {
    uploadRunRef.current += 1;
    if (uploadFileData.uploadId) {
      cancelMultiPartUpload({ id: uploadFileData.uploadId })
        .then((res: { cancelMultiPart: { success: boolean; message: string } }) => {
          if (res.cancelMultiPart.success) {
            setUploadStatus(null);
            setFileList([]);
            setUploadFileData(getDefaultFileUploadData());
            setUploadPercentage(0);
            onVideoAdd?.(false);
            setVideoAdded(false);
          }
          else {
            throw new Error(res.cancelMultiPart.message);
          }
        })
        .catch(() => {
          notification.error({ message: 'Error occurred in cancel file upload' });
        });
    }
  }, [cancelMultiPartUpload, onVideoAdd, uploadFileData.uploadId]);

  const handleModalClose = useCallback(() => {
    if (uploadStatus === UploadStatusEnum.UPLOADING && uploadFileData.uploadId) cancelFileUpload();
    setVisible(false);
  }, [cancelFileUpload, uploadStatus, uploadFileData.uploadId]);
  const handleModalOpen = useCallback(() => setVisible(true), []);

  const deleteFile = useCallback(() => {
    const form = getForm();
    const newData: any = {
      [name]: null,
      duration: null,
    };
    if (dataField) newData[dataField] = null;
    form?.setFieldsValue(newData);
  }, [getForm, name, dataField]);

  const getPreviewItem = (url: string, index: number, isLargePreview = false) => {
    if (type === 'CSV') {
      return (
        <div key={index} className="preview-item">
          <FileTextOutlined
            className="csv-preview"
            style={{ border: `1px solid ${token.colorBorder}`, color: token.colorSuccess }}
          />
          <div className="preview-overlay">
            <Tooltip title="Preview">
              <Button type="text" icon={<EyeOutlined />} href={url} target="_blank" />
            </Tooltip>
            {!disabled ? (
              <Tooltip title="Delete">
                <Button type="text" icon={<DeleteOutlined />} onClick={() => deleteFile()} />
              </Tooltip>
            ) : null}
          </div>
        </div>
      );
    }
    return (
      <div key={index} className={classnames('preview-item', { large: isLargePreview })}>
        {isLargePreview ? (
          <video height="600" controls autoPlay>
            <source src={url} type="video/mp4" />
          </video>
        ) : (
          <>
            <div className={classnames('video-container', { bordered: true })}>
              {
                <video height="200">
                  <source src={url} type="video/mp4" />
                </video>
              }
            </div>
            <div className="preview-overlay">
              <Tooltip title="Preview">
                <Button
                  type="text"
                  icon={<EyeOutlined />}
                  onClick={() => {
                    setPreviewItem(url);
                  }}
                />
              </Tooltip>
              {!disabled ? (
                <Tooltip title="Delete">
                  <Button type="text" icon={<DeleteOutlined />} onClick={() => deleteFile()} />
                </Tooltip>
              ) : null}
            </div>
          </>
        )}
      </div>
    );
  };
  const triggerEl = useMemo(() => {
    if (trigger) {
      return (
        <>
          <Form.Item name={name} label={label} tooltip={tooltip} rules={rules} hidden>
            <Input />
          </Form.Item>
          {cloneElement(trigger, { onClick: handleModalOpen, disabled: true })}
        </>
      );
    }
    return (
      <div className="upload-form-item">
        <Space align="center" size="large">
          <Form.Item name={name} label={label} tooltip={tooltip} rules={rules}>
            <Input type="hidden" />
          </Form.Item>

          <Button type="primary" onClick={handleModalOpen} disabled={disabled}>
            Select File
          </Button>
        </Space>
      </div>
    );
  }, [trigger, name, label, tooltip, rules, disabled, handleModalOpen]);

  return (
    <>
      {triggerEl}

      {dataField ? (
        <div className="preview-list">
          <Form.Item
            noStyle
            shouldUpdate={(prevVal, currVal) => !isEqual(prevVal[name], currVal[name])}
          >
            {({ getFieldValue }) => {
              const files = getFieldValue(dataField);
              const fileArr: FileData[] = files && !isEmpty(files) ? castArray(files) : [];
              return fileArr[0] ? [getPreviewItem(videoUrl || fileArr[0].url, 0)] : [];
            }}
          </Form.Item>
        </div>
      ) : null}

      <Modal
        title="Preview"
        open={!!previewItem}
        width={800}
        footer={null}
        onCancel={() => {
          setPreviewItem(null);
        }}
        destroyOnClose
      >
        {previewItem && getPreviewItem(previewItem, 0, true)}
      </Modal>

      <Modal
        title={
          <div>
            <span>Upload {startCase(type)}</span>
            {titleExtra || null}
          </div>
        }
        visible={visible}
        onCancel={handleModalClose}
        cancelButtonProps={{
          disabled: !(uploadStatus === UploadStatusEnum.UPLOADING && uploadFileData.uploadId),
        }}
        okText={uploadFileData.failedRetryCount > MAX_RETRY ? 'Retry' : 'Upload'}
        onOk={uploadFiles}
        confirmLoading={uploadStatus === UploadStatusEnum.UPLOADING}
        okButtonProps={{
          disabled: !!(uploadStatus === UploadStatusEnum.UPLOADING && uploadFileData.uploadId),
        }}
        width={600}
        wrapClassName="upload-modal-wrapper"
        destroyOnClose
      >
        <Upload.Dragger
          multiple={false}
          fileList={fileList}
          maxCount={MAX_UPLOAD_COUNT}
          beforeUpload={handleBeforeUpload}
          onRemove={handleOnRemove}
          listType="picture"
          accept={MIMES[type].types.join(',')}
          disabled={uploadStatus === UploadStatusEnum.UPLOADING}
        >
          <p className="ant-upload-drag-icon">
            <FaIcon icon={faInbox} size="lg" />
          </p>
          <p className="ant-upload-text">Click or drag file to this area</p>
          <p className="ant-upload-hint">
            Supported Types:
            {MIMES[type].extensions.join(', ')}
          </p>
        </Upload.Dragger>

        {uploadStatus === UploadStatusEnum.UPLOADING && uploadPercentage ? (
          <Progress
            percent={uploadPercentage}
            status={uploadPercentage === -1 ? 'exception' : 'active'}
          />
        ) : null}
      </Modal>
    </>
  );
}

export default FormMultiPartUpload;
